/**
 * Inline maths markup for question content.
 *
 * Authors write LaTeX directly inside the plain-text content blocks using
 * dollar delimiters — `$...$` for inline maths and `$$...$$` for a display
 * (centred, block) equation. A literal dollar sign is escaped as `\$`.
 *
 * This module is the single source of truth for how that markup is tokenised,
 * so the web (KaTeX) and PDF (MathJax) renderers stay consistent, and the zod
 * schema can reject unbalanced delimiters before a question is saved.
 *
 * @format
 */

/** A single tokenised run of a content string: either literal prose or maths. */
export interface MathSegment {
	type: "text" | "math";
	/** For `text`, the literal prose (with `\$` already unescaped to `$`). For
	 * `math`, the raw LaTeX source between the delimiters. */
	value: string;
	/** `true` for `$$...$$` display maths; `false` for inline `$...$` and prose. */
	display: boolean;
}

/**
 * Locate the closing delimiter that matches an opening one, honouring escaped
 * dollars (`\$`). Returns the index of the closing delimiter's first `$`, or
 * `-1` when the run is never closed.
 */
const findClose = (input: string, start: number, canDisplay: boolean): number => {
	let i = start;
	while (i < input.length) {
		if (input[i] === "\\" && input[i + 1] === "$") {
			i += 2;
			continue;
		}
		if (input[i] === "$") {
			if (canDisplay) {
				// A display run only closes on a second `$$`; a lone `$` inside is
				// treated as content (authors escape literal dollars as `\$`).
				if (input[i + 1] === "$") {
					return i;
				}
				i += 1;
				continue;
			}
			return i;
		}
		i += 1;
	}
	return -1;
};

/**
 * Split a content string into ordered text/maths segments. Unterminated
 * delimiters degrade gracefully: the stray `$` is emitted as literal text so a
 * half-typed formula never throws at render time.
 */
export const parseMathSegments = (input: string): MathSegment[] => {
	const segments: MathSegment[] = [];
	let text = "";
	let i = 0;

	const flushText = () => {
		if (text) {
			segments.push({ type: "text", value: text, display: false });
			text = "";
		}
	};

	while (i < input.length) {
		const ch = input[i];
		if (ch === "\\" && input[i + 1] === "$") {
			text += "$";
			i += 2;
			continue;
		}
		if (ch === "$") {
			const canDisplay = input[i + 1] === "$";
			const openLen = canDisplay ? 2 : 1;
			const closeIndex = findClose(input, i + openLen, canDisplay);
			if (closeIndex === -1) {
				// No matching close — keep the delimiter as literal prose.
				text += ch;
				i += 1;
				continue;
			}
			flushText();
			segments.push({ type: "math", value: input.slice(i + openLen, closeIndex), display: canDisplay });
			i = closeIndex + openLen;
			continue;
		}
		text += ch;
		i += 1;
	}

	flushText();
	return segments;
};

/** `true` when the string contains at least one `$...$` / `$$...$$` run. */
export const containsMath = (input: string): boolean =>
	parseMathSegments(input).some((segment) => segment.type === "math");

/**
 * Validate that every opening maths delimiter has a matching close. Used by the
 * zod schema to warn authors about a stray or unbalanced `$` before saving.
 */
export const hasBalancedMathDelimiters = (input: string): boolean => {
	let i = 0;
	while (i < input.length) {
		if (input[i] === "\\" && input[i + 1] === "$") {
			i += 2;
			continue;
		}
		if (input[i] === "$") {
			const canDisplay = input[i + 1] === "$";
			const openLen = canDisplay ? 2 : 1;
			const closeIndex = findClose(input, i + openLen, canDisplay);
			if (closeIndex === -1) {
				return false;
			}
			i = closeIndex + openLen;
			continue;
		}
		i += 1;
	}
	return true;
};
